import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom, Observable, tap } from 'rxjs';
import { Default_SessionValue, SystemValues } from '../models/core.model';
import { FillGenericModalPicklistPayLoad } from '../models/models';
import { CoreApiService } from './core-api.service';

export interface AppConfig {
  apiUrl: string;
  appVersion: string;
  environment: 'development' | 'production';
}

@Injectable({
  providedIn: 'root'
})
export class CoreService {

  private coreApiService = inject(CoreApiService)
  private configSubject = new BehaviorSubject<AppConfig>({
    apiUrl: 'https://api.example.com',
    appVersion: '1.0.0',
    environment: 'development'
  });

  private loadingSubject = new BehaviorSubject<boolean>(false);
  private systemValuesSubject = new BehaviorSubject<SystemValues | null>(null);

  public config$ = this.configSubject.asObservable();
  public loading$ = this.loadingSubject.asObservable();

  constructor() {
    this.initializeApp();
  }

  private initializeApp(): void {
    console.log('CoreService: Application initialized');
  }

  public setLoading(loading: boolean): void {
    this.loadingSubject.next(loading);
  }

  public updateConfig(config: Partial<AppConfig>): void {
    const currentConfig = this.configSubject.value;
    this.configSubject.next({ ...currentConfig, ...config });
  }

  public getConfig(): AppConfig {
    return this.configSubject.value;
  }

  public logError(error: any, context?: string): void {
    const errorMessage = `[CoreService Error]${context ? ` ${context}:` : ''} ${error}`;
    console.error(errorMessage);
  }

  public logInfo(message: string, data?: any): void {
    console.log(`[CoreService Info] ${message}`, data);
  }

  public addCaseInfoPageLoadLog(fileNumber: string, defedantNumber: string) {
    this.coreApiService.addCasePageLoadLog(fileNumber, defedantNumber).subscribe();
  }

  public refereshSystemValues(): Observable<SystemValues> {
    return this.coreApiService.getSystemValues().pipe(tap(data => this.systemValuesSubject.next(data)));
  }

  public get systemValues(): SystemValues {
    return this.systemValuesSubject.value || Default_SessionValue;
  }

  public GetValue(tableName: string, columnName: string, where: string) {
    return this.coreApiService.GetValue(tableName, columnName, where);
  }

  public IsTableHasRecord(tableName: string, columnName: string) {
    return this.coreApiService.IsTableHasRecord(tableName, columnName);
  }

  public isSmallScreen() {
    return window.innerHeight <= 900;
  }

  public GetValueSorted(tableName: string, columnName: string, where: string, sortBy: string) {
    return this.coreApiService.GetValueSorted(tableName, columnName, where, sortBy);
  }

  public IsExistWhere(tableName: string, columnName: string, columnValue: string, useQuote: boolean, where: string) {
    return this.coreApiService.IsExistWhere(tableName, columnName, columnValue, where, useQuote);
  }

  public FillGenericModalPicklistLazyLoad(requestPayload: FillGenericModalPicklistPayLoad) {
    return this.coreApiService.FillGenericModalPicklistLazyLoad(requestPayload);
  }

  public FillGenericModalPicklist(setgenericmodalpicklistperamater: number, whereCondition: string) {
    return this.coreApiService.FillGenericModalPicklist(setgenericmodalpicklistperamater, whereCondition);
  }

  public getPdName(pdName: string) {
    return this.coreApiService.getPdName(pdName);
  }

  public getCoDefendantsForAttachment(params: { caseId: string; parentPage: string; }) {
    return this.coreApiService.getCoDefendantsForAttachment(params);
  }
  
  public loadBuildVersion() {
    return this.coreApiService.loadBuildVersion();
  }
  
  public isCivilCase(fileNumber: string) {
    return this.coreApiService.isCivilCase(fileNumber);
  }
}